/* The report, as a MODAL over another screen. KANON `ankiety-klienta-2026-09-16`, view 02.
 *
 * ── Recovered rather than reinvented ────────────────────────────────────────────────────────
 * This is `pages/client-detail/ui/SurveyDialog.module.css` as it stood at `9d6072b`, before the
 * client detail's report stopped being a modal on 16 September 2026 and the file was deleted. What
 * came back is the SHELL — the scrim, the popup box, its `dvh` ceiling, the head with its icon row
 * and the scrolling body. What did NOT come back is that dialog's two-column body: the report's own
 * bands are `SurveyReport.module.css` now, drawn identically here and in the client detail's pane,
 * which is the whole point of the feature this file sits in.
 *
 * The scrim, the radius and the shadow are the create dialog's own
 * (`features/create-plan/ui/CreatePlanDialog.module.css`): `--scrim`, `--radius-20px`,
 * `--shadow-modal`, and the entrance is Base UI's `[data-starting-style]` — an opacity fade and 4px
 * of rise, no keyframes, and `tokens/base.css` collapses every duration under reduced motion.
 *
 * NOT `--shadow-pop`: on the Flame themes that token carries a coloured blur, and a coloured shadow
 * is banned outright. */

.backdrop {
  position: fixed;
  inset: var(--space-0);
  z-index: 30;
  background: var(--scrim);
  transition-duration: var(--dur-fast);
  transition-property: opacity;
  transition-timing-function: var(--ease);
}

.backdrop[data-starting-style] {
  opacity: 0;
}

/* Centred by translation rather than by flexbox on the backdrop: the two are siblings under the
 * portal, not parent and child, so there is no box to centre within.
 *
 * `dvh`, not `vh`, for the create dialog's own measured reason: on a laptop whose browser chrome
 * collapses on scroll, `vh` resolves against the taller viewport and the dialog overhangs the
 * window by the toolbar's height. */
.popup {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 31;
  display: flex;
  flex-direction: column;
  width: min(var(--w-survey-dialog), calc(100vw - var(--space-8)));
  max-height: min(88dvh, 940px);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-20px);
  outline: none;
  box-shadow: var(--shadow-modal);
  transform: translate(-50%, -50%);
  transition-duration: var(--dur-fast);
  transition-property: opacity, transform;
  transition-timing-function: var(--ease);
}

.popup[data-starting-style] {
  opacity: 0;
  transform: translate(-50%, calc(-50% + var(--space-1)));
}

/* ── the head ───────────────────────────────────────────────────────────────── */

.head {
  display: flex;
  flex: none;
  gap: var(--space-3);
  align-items: flex-start;
  padding: var(--space-5) var(--space-6);
  border-bottom: var(--border-hairline) solid var(--border);
}

.headText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-5px);
  min-width: 0;
}

.title {
  margin: var(--space-0);
  font-size: var(--fs-sheet-title);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-heading);
  color: var(--text);
  letter-spacing: var(--ls-modal);
}

.subtitle {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
  font-variant-numeric: tabular-nums;
}

.actions {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
}

/* GEOMETRY ONLY — the treatment is `shared/ui/iconButton.module.css`. */
.action {
  width: var(--h-detail-tiny-btn);
  height: var(--h-detail-tiny-btn);
  background: var(--bg-chip);
  border-radius: var(--radius-mark);
}

/* „Oznacz jako przejrzaną" is the one real ACT in this dialog, and it wears the same treatment as
 * the client detail pane's — `shared/ui/emberButton.module.css`, through `MarkReviewedButton`. So
 * this class is that file's required geometry and restates nothing the treatment declares: not the
 * fill, not the ink, not the border. */
.markAction {
  width: 32px;
  height: 32px;
  padding: var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-extrabold);
  letter-spacing: normal;
  border-radius: var(--radius-12px);
}

/* The ✕ stands apart from the pair beside it: two unlabelled squares touching read as one control,
 * and the misfire is not symmetrical — closing loses a dialog the trainer can reopen, while the
 * mark cannot be undone at all. */
.closeAction {
  margin-inline-start: var(--space-2);
}

/* ── the scrolling body ─────────────────────────────────────────────────────── */

.body {
  --sb-size: var(--space-2);
  --sb-radius: var(--radius-hair);

  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-gutter: stable;
}

/* The report's four bands, at the rhythm the bands are written for. The client detail's pane sets
 * the same gap from its own stylesheet — a band knows its rhythm and nothing about its host. */
.report {
  display: flex;
  flex-direction: column;
  gap: var(--space-26px);
  min-width: 0;
  padding: var(--space-5) var(--space-6) var(--space-6);
}

/* The FIRST band draws no rule here, and that is a difference between the two hosts rather than a
 * difference of opinion. Every band carries a `border-top`, because in the client detail's pane the
 * one under the head is what stops „ODCZYTY" reading as a third line of the sentence above it. This
 * popup's head already ends in a hairline, so the band's own drew a second line four pixels under
 * the first. The band keeps its rhythm; only its top edge is answered for by the surface it is on. */
.report > :first-child {
  padding-top: var(--space-0);
  border-top: none;
}

/* The body's reserve. It covers the WAIT, the FAILURE and the missing occurrence, so the popup is
 * the same height in every state it can be in and never resizes under the scrim once it is open. */
.bodyReserve {
  min-height: var(--h-survey-dialog-wait);
}

.state {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  align-items: center;
  justify-content: center;
  min-height: var(--h-survey-dialog-wait);
  padding: var(--space-6);
}

/* The wait, in the shape of a report: a head over answer blocks, inside the reserve above, so
 * drawing them moves nothing. */
.skelBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-5) var(--space-6) var(--space-6);
}

.skelHead {
  width: 40%;
  height: var(--h-survey-chip-tall);
}

.skelBlock {
  width: 100%;
  height: var(--h-survey-pane-block);
}
